import { useEffect, useRef, useState } from "react";
import Section from "../../layout/Section/Section";
import ServiceIcon from "../../ui/ServiceIcon/ServiceIcon";
import { services } from "../../../data/services";
import styles from "./ServicesOverview.module.css";

const serviceDetails = {
  gbp: { label: "Be discovered", title: "Google Business Profile", text: "Make a great first impression, right where your customers are searching." },
  maps: { label: "Be the local choice", title: "Google Maps optimization", text: "Help nearby customers find your location and choose your business." },
  "website-builder": { label: "Make it yours", title: "Business websites", text: "A polished home for your business, built to turn a visit into a conversation." },
};

function ServicePreview({ id }) {
  if (id === "gbp") {
    return (
      <div className={styles.listing}>
        <span className={styles.listingAvatar}><ServiceIcon id={id} /></span>
        <span className={styles.listingCopy}><strong>Your business</strong><span>Ready to be discovered</span></span>
        <span className={styles.verified}><svg viewBox="0 0 20 20"><path pathLength="1" d="m5 10 3 3 7-7" /></svg></span>
      </div>
    );
  }
  if (id === "maps") {
    return (
      <div className={styles.mapPreview}>
        <span className={styles.mapRoad} /><span className={styles.mapRoadSecond} /><span className={styles.mapBlock} />
        <span className={styles.mapPulse} /><span className={styles.mapPulseSecond} />
        <span className={styles.mapPin}><svg viewBox="0 0 24 24"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg></span>
        <span className={styles.mapLabel}>Your next customer is nearby</span>
      </div>
    );
  }
  return (
    <div className={styles.browser}>
      <div className={styles.browserBar}><i /><i /><i /><span>yourbusiness.in</span></div>
      <div className={styles.browserBody}>
        <div className={styles.browserCopy}><span /><span /><span /><i /></div>
        <div className={styles.browserArt}><ServiceIcon id={id} /></div>
      </div>
      <span className={styles.browserCursor}><svg viewBox="0 0 24 24"><path d="m5 3 14 11-7 1-4 6Z" /></svg></span>
    </div>
  );
}

export default function ServicesOverview() {
  const gridRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const previews = gridRef.current.querySelectorAll("[data-service-preview]");
    if (typeof IntersectionObserver === "undefined") {
      previews.forEach(preview => { preview.dataset.inView = "true"; });
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { entry.target.dataset.inView = String(entry.isIntersecting); });
    }, { threshold: 0.2 });
    previews.forEach(preview => observer.observe(preview));
    return () => observer.disconnect();
  }, []);

  return (
    <Section id="services" eyebrow="What we do" title="The right tools. More possibilities." subtitle="From the first search to the first hello, make every touchpoint count." bg="white">
      <div className={styles.animationControls}>
        <button className={styles.motionToggle} type="button" onClick={() => setPaused(value => !value)}>
          <svg viewBox="0 0 16 16" aria-hidden="true">{paused ? <path d="m5 3 7 5-7 5Z" /> : <path d="M5 3v10M11 3v10" />}</svg>
          {paused ? "Play animations" : "Pause animations"}
        </button>
      </div>
      <div className={styles.grid} ref={gridRef} data-paused={paused}>
        {services.map((service, index) => {
          const details = serviceDetails[service.id];
          return (
            <article key={service.id} className={styles.card}>
              <div className={styles.preview} data-service-preview aria-hidden="true">
                <div className={styles.previewLabel}><span>{details.label}</span><span>0{index + 1}</span></div>
                <ServicePreview id={service.id} />
              </div>
              <div className={styles.content}>
                <h3 className={styles.title}>{details.title}</h3>
                <p className={styles.desc}>{details.text}</p>
                <ul className={styles.items}>
                  {service.items.slice(0, 3).map((item) => (
                    <li key={item}><svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 8 2.5 2.5L12 5" /></svg>{item}</li>
                  ))}
                </ul>
                <a className={styles.link} href={`/services#service-${service.id}`}>Explore service <span aria-hidden="true">↗</span></a>
              </div>
            </article>
          );
        })}
      </div>
      <div className={styles.more}>
        <p>Start with what you need. Grow at your own pace.</p>
        <a href="/services">View all services <span aria-hidden="true">→</span></a>
      </div>
    </Section>
  );
}
